import * as React from 'react';
import { FilterAnchor, SkeletonRows, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { ENDING_CASE_VIEWS, ENDING_TYPE_LABEL } from '@chess/api-ending-case';
import { EndingCaseLegend } from '@chess/api-ending-case/react';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import type { EndingTypesResult } from './EndingTypesAggregator.ts';
import { rowFilter, segmentFilter } from './segment.ts';
import './ending-types.css';

// Your endings by type: "Your endings" split by the kind of ending, the
// books' material classes (rook ending, opposite-coloured bishops, …). One
// stacked bar per kind, its segments the same cases in the same order and
// colours as that card's pie (`ENDING_CASE_VIEWS`), its length the kind's
// games. Kinds that lost the most winning chances come first; with more than
// eight kinds, the rarest share an "Other" bar. A row's name opens the
// library filtered to its kinds (`rowFilter`), a segment to that kind and
// case (`segmentFilter`); drawn has no such filter and is not a link.
// Presentational: its data comes as props.

export type EndingTypesCardProps = CardProps<{ result: EndingTypesResult }, { link?: FilterLink }>;

export const TITLE = 'Your endings by type';
const ASIDE = 'most chances lost first';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const Legend: React.FC = () => <EndingCaseLegend layout="row" className="ending-types-legend" />;

export const EndingTypesCard: React.FC<EndingTypesCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardNote>
          <TextSkeleton sample="18 endings" />
        </CardNote>
        <div className="ending-types-rows">
          <SkeletonRows labels={[undefined, undefined, undefined]} value="12" label="Opposite-coloured bishops" />
        </div>
        <Legend />
      </>
    );
  const { result, link } = props;
  if (!result.games)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No endings in the last 30 days.</CardEmpty>
      </>
    );
  const max = Math.max(1, ...result.rows.map((r) => r.games));
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <CardNote>{plural(result.games, 'ending')}</CardNote>
      <div className="split-rows ending-types-rows">
        {result.rows.map((row) => (
          <div className="split-row" key={row.key}>
            <FilterAnchor
              className="split-label"
              link={link}
              filter={rowFilter(row.types)}
              title={`${row.key === 'other' ? `The rarer kinds of ending: ${row.types.map((t) => ENDING_TYPE_LABEL[t]).join(', ')}` : row.label} · opens those games`}
            >
              {row.label}
            </FilterAnchor>
            <span className="split-bar" style={{ width: `${(100 * row.games) / max}%` }}>
              {ENDING_CASE_VIEWS.map((v) => {
                const count = row.cases[v.case];
                if (!count) return null;
                const filter = segmentFilter(row.types, v.case);
                const title = `${row.label} · ${v.label}: ${plural(count, 'game')}${filter ? ' · opens those games' : ''}`;
                return filter ? (
                  <FilterAnchor key={v.case} className="split-segment" link={link} filter={filter} title={title} style={{ flex: count, background: v.colour }} />
                ) : (
                  <span key={v.case} className="split-segment" title={title} style={{ flex: count, background: v.colour }} />
                );
              })}
            </span>
            <span className="split-total">{row.games}</span>
          </div>
        ))}
      </div>
      <Legend />
    </>
  );
};
